import { CrudModes } from "@app/shared/components/abstracts/base-component";
import { AbstractAdapter, Adapter } from "@app/shared/models/adapter.model";
import { Field } from "@app/shared/models/form-field.model";

/**
 * Enumerator: viewStates
 *
 * View component states
 *
 * Value to control which component is currently being displayed
 */
export enum viewStates {
  viewList,
  viewForm,
  viewTranslationList,
  viewTranslationForm,
  viewEnvironmentsList,
  viewEnvironmentsForm,
  viewSharedComponentsList,
  viewSharedComponentsMaintenance,
  viewSharedComponentsScreenTranslationsList,
  viewSharedComponentsScreenTranslationsForm,
  viewDocumentsPlus
}

export const ApplicationTypes = {
  Main: "M",
  Administration: "A",
  Common: "C",
  Other: "O"
};

/**
 * Class: ApplicationParms
 *
 * Parameters used to for applications form
 */
export class ApplicationParms {
  constructor(
    public id: string,
    public mode: CrudModes,
    public description?: string
  ) {}
}

export class ApplicationEnvironmentsParms {
  constructor(
    public data: ApplicationParms,
    public env: ApplicationEnvironmentsList,
    public mode: CrudModes
  ) {}
}

/**
 * Object used for wbe services that require only the application id
 */
export interface ApplicationId {
  applicationId: string;
}

/**
 * Object used for list of applications
 */
export interface Applications {
  applicationId: string;
  shortDescription: string;
  longDescription: string;
  sequence: number;
  status: boolean;
  type: string;
  isSelected: boolean;

  _status: string;
  _type: string;
}

/**
 * Object used for form.
 */
export interface Application {
  applicationId: string;
  type: Field;
  sequence: Field;
  webfaceUrl: Field;
  status: Field;
  updateTimestamp: string;
}

export interface ApplicationEnvironments {
  applicationId: string;
  environmentList: ApplicationEnvironmentsList[];
}

export interface ApplicationEnvironmentsList {
  id: string;
  description: string;
  rPlusEnvironmentId: string;
  status: string;
  isSelected?: boolean;
  _status?: string;
}

export interface ApplicationEnvironment {
  applicationId: string;
  environmentId: Field;
  rPlusEnvironmentId: Field;
}

export interface ApplicationEnviromentsMaster {
  applicationId: string;
  environmentMasterList: ApplicationEnviromentsMasterList[];
}

export interface ApplicationEnviromentsMasterList {
  id: string;
  description: string;
}

export interface ApplicationEnviromentsReportsPlus {
  id: string;
}

export interface ApplicationSharedComponentsList {
  id: string;
  description: string;
  isSelected?: boolean;
}

/**
 * Class: ApplicationsAdapter
 *
 * Implements a data adapter for all applications the client has purchased.
 */
export class ApplicationsAdapter extends AbstractAdapter<Applications[]> {
  adapt(rawData: any): Applications[] {
    (rawData.appList as Applications[]).map((e) => {
      e._status = e.status ? this.translate("APP_APPLICATIONS.LBL_ACTIVE") : this.translate("APP_APPLICATIONS.LBL_DISABLED");

      switch (e.type) {
        case ApplicationTypes.Main: {
          e._type = this.translate("APP_APPLICATIONS.LBL_MAIN");
          break;
        }
        case ApplicationTypes.Administration: {
          e._type = this.translate("APP_APPLICATIONS.LBL_ADMIN");
          break;
        }
        case ApplicationTypes.Common: {
          e._type = this.translate("APP_APPLICATIONS.LBL_COMMON");

          break;
        }
        case ApplicationTypes.Other: {
          e._type = this.translate("APP_APPLICATIONS.LBL_OTHER");
          break;
        }
        default: {
          e._type = "";
        }
      }

      e.isSelected = false;
      return e;
    });

    return rawData.appList;
  }
}

/**
 * Class: ApplicationAdapter
 *
 * Implements a data adapter for an individual applications the client has purchased.
 */
export class ApplicationAdapter implements Adapter<Application> {
  adapt(rawData: any): Application {
    return rawData;
  }
}

/**
 * Class: ApplicationEnvironmentsAdapter
 *
 * Implements a data adapter for application environments list
 */
export class ApplicationEnvironmentsAdapter extends AbstractAdapter<ApplicationEnvironments> {
  adapt(rawData: ApplicationEnvironments): ApplicationEnvironments {
    rawData.environmentList.map((e) => {
      e._status = e.status ? this.translate("APP_APPLICATIONS.LBL_ACTIVE") : this.translate("APP_APPLICATIONS.LBL_DISABLED");

      e.isSelected = false;
      return e;
    });

    return rawData;
  }
}

export class ApplicationEnvironmentAdapter extends AbstractAdapter<ApplicationEnvironment> {
  adapt(rawData: any): ApplicationEnvironment {
    return rawData;
  }
}

export class ApplicationEnviromentsMasterListAdapter extends AbstractAdapter<ApplicationEnviromentsMaster> {
  adapt(rawData: any): ApplicationEnviromentsMaster {
    return rawData;
  }
}

export class ApplicationEnviromentsReportsPlusListAdapter extends AbstractAdapter<ApplicationEnviromentsReportsPlus[]> {
  adapt(rawData: any): ApplicationEnviromentsReportsPlus[] {
    return rawData?.environmentRplusList;
  }
}
